import { useQuery } from '@tanstack/react-query';
import { apiRoutes } from '../../config/api/apiRoutes';
import axios from '../../libraries/axios/axios';
import { queryKeys } from '../keys';
import { StaticPage, StaticPageType } from '../../domain/StaticPage';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { staticPagesState } from '../../atoms/static-page/static-pages.atom';
import { entityTranslationsState } from '../../atoms/entity-translation/entity-translation.atom';
import { translateEntity } from '../../utils/entity-translation/entity-translation';

export const useStaticPagesQuery = () => {
  const entityTranslations = useRecoilValue(entityTranslationsState);
  const setStaticPages = useSetRecoilState(staticPagesState);

  const fetchStaticPages = async (): Promise<StaticPage[]> => {
    const { data } = await axios.get(apiRoutes.staticPages.getAll);

    const translatedStaticPages = (data as StaticPage[]).map((staticPage) => ({
      ...staticPage,
      title: translateEntity(
        entityTranslations,
        staticPage.id,
        'StaticPage',
        'title',
        staticPage.title,
      ),
      content: translateEntity(
        entityTranslations,
        staticPage.id,
        'StaticPage',
        'content',
        staticPage.content,
      ),
      seo: {
        slug: translateEntity(
          entityTranslations,
          staticPage.id,
          'StaticPage',
          'seo.slug',
          staticPage.seo?.slug ?? '',
        ),
        title: translateEntity(
          entityTranslations,
          staticPage.id,
          'StaticPage',
          'seo.title',
          staticPage.seo?.title ?? '',
        ),
        description: translateEntity(
          entityTranslations,
          staticPage.id,
          'StaticPage',
          'seo.description',
          staticPage.seo?.description ?? '',
        ),
      },
    }));

    setStaticPages(translatedStaticPages);

    return translatedStaticPages;
  };

  return useQuery({
    queryKey: queryKeys.staticPages.all,
    queryFn: fetchStaticPages,
  });
};

export const useStaticPageNameQuery = (name: StaticPageType) => {
  const fetchStaticPage = async (name: StaticPageType): Promise<StaticPage> => {
    const { data } = await axios.get(
      apiRoutes.staticPages.get.replace(':name', name),
    );

    return data;
  };

  return useQuery({
    queryKey: queryKeys.staticPages.byName(name),
    queryFn: () => fetchStaticPage(name),
  });
};
